import { useMemo } from "react";
import { get } from "lodash-es";
import { useUTLSettingsContext } from "@dexteel/mesf-core";
import {
  useAllLocationsData,
  useRefreshTimer,
  useSearchIntegration,
  useSpecialLocationsOrdering,
} from "../";
import { TOTAL_PAGES } from "../../constants";
import { Location, SpecialLocation, Group } from "../../types";
import { SearchResults, GroupTotal, LocationTotal } from "../../models";

interface UseBilletYardDataProps {
  regularLocationPage: number;
  searchCriteria: {
    locations: string[];
    heats: string[];
    sections: string[];
    lengths: string[];
    grades: string[];
    qualities: string[];
    destinations: string[];
  };
  onPageChange: (direction: number) => void;
  onModalClose?: () => void;
}

interface UseBilletYardDataReturn {
  // Raw data from API
  allLocations: Location[];
  specialLocations: SpecialLocation[];
  groupsByLocationId: Record<number, Group[]>;
  groupTotalsByLocationId: Record<number, Record<string, GroupTotal>>;
  locationTotalsById: Record<number, LocationTotal>;

  // Loading states
  isLoading: boolean;
  isRefreshing: boolean;
  error: string;
  setError: (error: string) => void;

  // Timer data
  refreshTime: number;
  getSeconds: () => number;
  isTimerRefreshing: boolean;
  pauseFetchData: () => void;
  resumeFetchData: () => void;

  // Processed data
  regularLocations: Location[];
  orderedSpecialLocations: SpecialLocation[];

  // Search functionality
  searchResults: SearchResults | null;
  isSearchActive: boolean;
  handleSearchResults: (results: SearchResults | null) => void;
  handleClearSearch: () => void;
  isLocationSearchHighlighted: (locationId: number) => boolean;
  getSearchMatchingGroupIds: (
    locationId: number,
    groupsByLocationId: Record<number, Group[]>,
  ) => number[];
  matchingPages: number[];

  // Combined location data for search
  allLocationsForSearch: Location[];

  // Data availability helpers
  hasData: boolean;
  hasLocations: boolean;
  hasSpecialLocations: boolean;
}

export const useBilletYardData = ({
  regularLocationPage,
  searchCriteria,
  onPageChange,
  onModalClose,
}: UseBilletYardDataProps): UseBilletYardDataReturn => {
  // Timer configuration from settings
  const { state } = useUTLSettingsContext();
  const refreshTime = parseInt(get(state.settings, "BYI_APP_REFRESH_TIME", 5));

  // Core data fetching
  const {
    locations: allLocations,
    specialLocations,
    groupsByLocationId,
    groupTotalsByLocationId,
    locationTotalsById,
    isLoading,
    isRefreshing,
    error,
    setError,
    fetchData,
  } = useAllLocationsData();

  // Refresh timer management
  const {
    getSeconds,
    isRefreshing: isTimerRefreshing,
    pause: pauseFetchData,
    resume: resumeFetchData,
  } = useRefreshTimer(refreshTime, fetchData);

  // Special locations ordering
  const { orderedSpecialLocations } = useSpecialLocationsOrdering({
    specialLocations,
  });

  // Memoize regular locations for current page to avoid unnecessary re-renders
  const regularLocations = useMemo(() => {
    if (allLocations.length === 0) return [];

    const startIndex = (regularLocationPage - 1) * 7;
    const endIndex = startIndex + 7;
    return allLocations.slice(startIndex, endIndex);
  }, [allLocations, regularLocationPage]);

  // Combined location data for search (regular + special)
  const allLocationsForSearch = useMemo(() => {
    return [...allLocations, ...specialLocations];
  }, [allLocations, specialLocations]);

  // Search integration with auto-close modal
  const {
    searchResults,
    isSearchActive,
    handleSearchResults,
    handleClearSearch: hookHandleClearSearch,
    isLocationSearchHighlighted,
    getSearchMatchingGroupIds,
    matchingPages,
  } = useSearchIntegration({
    locations: allLocationsForSearch,
    regularLocations: allLocations, // Only regular locations for page calculation
    groupsByLocationId,
    totalPages: TOTAL_PAGES,
    onPageChange,
    onModalClose: onModalClose || (() => {}),
    searchCriteria,
  });

  // Data availability helpers
  const hasLocations = allLocations.length > 0;
  const hasSpecialLocations = orderedSpecialLocations.length > 0;
  const hasData = hasLocations || hasSpecialLocations;

  // Stable clear search handler
  const handleClearSearch = () => {
    hookHandleClearSearch();
  };

  return {
    // Raw data from API
    allLocations,
    specialLocations,
    groupsByLocationId,
    groupTotalsByLocationId,
    locationTotalsById,

    // Loading states
    isLoading,
    isRefreshing,
    error,
    setError,

    // Timer data
    refreshTime,
    getSeconds,
    isTimerRefreshing,
    pauseFetchData,
    resumeFetchData,

    // Processed data
    regularLocations,
    orderedSpecialLocations,

    // Search functionality
    searchResults,
    isSearchActive,
    handleSearchResults,
    handleClearSearch,
    isLocationSearchHighlighted,
    getSearchMatchingGroupIds,
    matchingPages,

    // Combined location data for search
    allLocationsForSearch,

    // Data availability helpers
    hasData,
    hasLocations,
    hasSpecialLocations,
  };
};
